import { API_CONFIG } from '../config';
import { getAuthToken } from '../ApiService';

// ─── Types ────────────────────────────────────────────────────────────────

export interface TerritorialContext {
  score_reseau_communautaire?: number;
  appartenance_association?: number; // 0 or 1
  score_reputation_terrain?: number;
  diversification_activites?: number;
  exposition_zones_instables?: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface TerritorialContextResponse {
  success: boolean;
  data: TerritorialContext;
  message: string;
}

export interface ScoreChange {
  previous: number;
  change: number;
  direction: 'up' | 'down' | 'stable';
}

export interface CreditScoreComponents {
  cashFlowQuality: number;
  businessStability: number;
  financialHealth: number;
  paymentBehavior: number;
  growthTrend: number;
}

export interface RiskAssessment {
  level: string;
  factors: string[];
  recommendations: string[];
}

export interface CreditScoreMetadata {
  modelVersion: string;
  dataSource: string;
  confidenceScore: number;
  dataQualityScore: number;
  calculatedAt: string;
  validUntil: string;
}

export interface CreditScoreResult {
  score: number;
  riskLevel: string;
  scoreClass: string;
  components: CreditScoreComponents;
  riskAssessment: RiskAssessment;
  metadata: CreditScoreMetadata;
}

export interface CreditScoreHistoryItem {
  id: string;
  score: number;
  riskLevel?: string;
  scoreClass?: string;
  trigger: string;
  calculatedBy?: string;
  calculatedAt: string;
  validUntil?: string;
  fiscalYearId?: string;
  probabilityDefault?: number;
  confidenceScore?: number;
  modelVersion?: string;
  scoreChange?: ScoreChange;
}

export interface CreditScoreStats {
  currentScore: number;
  averageScore: number;
  minScore: number;
  maxScore: number;
  totalCalculations: number;
  lastCalculatedAt: string;
  trend: 'up' | 'down' | 'stable';
}

// ─── Company-scoped fetch helper ──────────────────────────────────────────
// Allows making API calls with a specific X-Company-Id header
// without interfering with the global auditor company context.

async function fetchWithCompany<T>(
  endpoint: string,
  companyId: string,
  options?: RequestInit & { skipParse?: boolean }
): Promise<{ success: boolean; data?: T; error?: string }> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0',
    'X-Company-Id': companyId,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${endpoint}`, {
      ...options,
      headers: { ...headers, ...(options?.headers as Record<string, string> ?? {}) },
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => null);
      return { success: false, error: errData?.message || `HTTP ${response.status}` };
    }

    const json = await response.json();

    // Handle wrapped responses { success, data, ... }
    if (typeof json === 'object' && 'success' in json && 'data' in json) {
      return { success: json.success, data: json.data as T };
    }
    if (typeof json === 'object' && 'data' in json) {
      return { success: true, data: json.data as T };
    }
    return { success: true, data: json as T };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

// ─── API ──────────────────────────────────────────────────────────────────

export const creditScoreApi = {
  /**
   * GET /credit-score/stats — aggregated statistics for a company
   */
  getStats: (companyId: string) =>
    fetchWithCompany<{ stats: CreditScoreStats }>(
      `/credit-score/stats?companyId=${encodeURIComponent(companyId)}`,
      companyId
    ),

  /**
   * GET /credit-score/history — score history for a company
   */
  getHistory: (companyId: string, params?: { fiscalYearId?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams({ companyId });
    if (params?.fiscalYearId) qs.set('fiscalYearId', params.fiscalYearId);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    return fetchWithCompany<{ history: CreditScoreHistoryItem[]; total: number }>(
      `/credit-score/history?${qs.toString()}`,
      companyId
    );
  },

  /**
   * PATCH /credit-score/territorial-context — save CET variables
   */
  updateTerritorialContext: (companyId: string, data: Partial<TerritorialContext>) =>
    fetchWithCompany<TerritorialContext>(
      '/credit-score/territorial-context',
      companyId,
      { method: 'PATCH', body: JSON.stringify(data) }
    ),

  /**
   * POST /credit-score/calculate — trigger score recalculation
   */
  calculate: (companyId: string, params?: { startDate?: string; endDate?: string }) =>
    fetchWithCompany<{ creditScore: CreditScoreResult; message: string }>(
      '/credit-score/calculate',
      companyId,
      { method: 'POST', body: JSON.stringify({ companyId, ...params }) }
    ),
};
